import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Building2, Pin, PinOff, Check, Search } from 'lucide-react';
import { useOrganization } from '@/contexts/OrganizationContext';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

interface OrganizationsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPinnedChange: (pinned: string[]) => void;
}

export const OrganizationsModal = ({
  open,
  onOpenChange,
  onPinnedChange,
}: OrganizationsModalProps) => {
  const { 
    currentOrg, 
    availableOrgs, 
    switchOrganization 
  } = useOrganization();
  
  const [pinnedOrgs, setPinnedOrgs] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    // Carregar organizações fixadas do localStorage
    const stored = localStorage.getItem('pinned_organizations');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setPinnedOrgs(parsed);
      } catch {
        setPinnedOrgs([]);
      }
    }
  }, []);

  const handleTogglePin = (orgId: string) => {
    const newPinned = pinnedOrgs.includes(orgId)
      ? pinnedOrgs.filter(id => id !== orgId)
      : [...pinnedOrgs, orgId];
    
    setPinnedOrgs(newPinned);
    localStorage.setItem('pinned_organizations', JSON.stringify(newPinned));
    onPinnedChange(newPinned);
  };

  const handleSwitchOrganization = async (orgId: string) => {
    await switchOrganization(orgId);
    onOpenChange(false);
  };

  const getRoleBadgeVariant = (role: string) => {
    switch (role) {
      case 'admin':
      case 'owner':
        return 'default';
      case 'manager':
        return 'secondary';
      default:
        return 'outline';
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'admin':
        return 'Admin';
      case 'owner':
        return 'Proprietário';
      case 'manager':
        return 'Gerente';
      case 'rep':
        return 'Rep';
      default:
        return role;
    }
  };

  // Filtrar organizações por busca
  const filteredOrgs = availableOrgs.filter(org =>
    org.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Separar organizações fixadas e outras
  const pinnedOrganizations = filteredOrgs.filter(org => pinnedOrgs.includes(org.id));
  const otherOrganizations = filteredOrgs.filter(org => !pinnedOrgs.includes(org.id));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100vw-2rem)] sm:w-[700px] md:w-[800px] max-w-[90vw] max-h-[85vh] !flex !flex-col overflow-hidden p-6 organizations-modal" style={{ display: 'flex' }}>
        <DialogHeader className="flex-shrink-0 pb-4">
          <DialogTitle>Organizações</DialogTitle>
          <DialogDescription>
            Selecione uma organização ou fixe as mais usadas para acesso rápido
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 flex-1 flex flex-col min-h-0 overflow-hidden">
          {/* Barra de busca */}
          <div className="relative flex-shrink-0">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            <Input
              placeholder="Buscar organizações..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 hover:border-[hsl(var(--brand-cyan))]/50 focus:border-[hsl(var(--brand-cyan))] focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none transition-colors"
            />
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden standard-scroll">
            <div className="space-y-4 pr-6 pb-4">
              {/* Organizações fixadas */}
              {pinnedOrganizations.length > 0 && (
                <div className="space-y-2">
                  <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide px-2">
                    Organizações Fixadas
                  </h3>
                  <div className="space-y-1">
                    {pinnedOrganizations.map((org) => (
                      <div
                        key={org.id}
                        className={cn(
                          "flex items-center gap-3 p-3 rounded-lg border transition-all cursor-pointer w-full organization-card",
                          currentOrg?.id === org.id 
                            ? "bg-muted border-primary" 
                            : "hover:bg-muted/50 hover:border-border"
                        )}
                        onClick={() => currentOrg?.id !== org.id && handleSwitchOrganization(org.id)}
                      >
                        <div className="flex items-center space-x-3 flex-1 min-w-0 overflow-hidden">
                          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 flex-shrink-0">
                            <Building2 className="h-5 w-5 text-primary" />
                          </div>
                          <div className="flex flex-col min-w-0 flex-1 overflow-hidden">
                            <span className="text-sm font-medium truncate">{org.name}</span>
                            <Badge 
                              variant={getRoleBadgeVariant(org.role)} 
                              className="text-xs h-4 self-start mt-1 w-fit"
                            >
                              {getRoleLabel(org.role)}
                            </Badge>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0 ml-2" onClick={(e) => e.stopPropagation()}>
                          {currentOrg?.id === org.id && (
                            <Check className="h-4 w-4 text-primary flex-shrink-0" />
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 hover:bg-muted transition-colors flex-shrink-0 group"
                            onClick={() => handleTogglePin(org.id)}
                            title="Desfixar organização"
                          >
                            <Pin className="h-4 w-4 text-[hsl(var(--brand-cyan))] fill-[hsl(var(--brand-cyan))] group-hover:opacity-80 transition-opacity" />
                          </Button>
                          <Button
                            variant={currentOrg?.id === org.id ? "default" : "outline"}
                            size="sm"
                            onClick={() => handleSwitchOrganization(org.id)}
                            disabled={currentOrg?.id === org.id}
                            className="whitespace-nowrap flex-shrink-0 text-xs px-3 transition-colors organization-select-button"
                          >
                            {currentOrg?.id === org.id ? 'Atual' : 'Selecionar'}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Outras organizações */}
              {otherOrganizations.length > 0 && (
                <div className="space-y-2">
                  {pinnedOrganizations.length > 0 && (
                    <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide px-2">
                      Outras Organizações
                    </h3>
                  )}
                  <div className="space-y-1">
                    {otherOrganizations.map((org) => (
                      <div
                        key={org.id}
                        className={cn(
                          "flex items-center gap-3 p-3 rounded-lg border transition-all cursor-pointer w-full organization-card",
                          currentOrg?.id === org.id 
                            ? "bg-muted border-primary" 
                            : "hover:bg-muted/50 hover:border-border"
                        )}
                        onClick={() => currentOrg?.id !== org.id && handleSwitchOrganization(org.id)}
                      >
                        <div className="flex items-center space-x-3 flex-1 min-w-0 overflow-hidden">
                          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 flex-shrink-0">
                            <Building2 className="h-5 w-5 text-primary" />
                          </div>
                          <div className="flex flex-col min-w-0 flex-1 overflow-hidden">
                            <span className="text-sm font-medium truncate">{org.name}</span>
                            <Badge 
                              variant={getRoleBadgeVariant(org.role)} 
                              className="text-xs h-4 self-start mt-1 w-fit"
                            >
                              {getRoleLabel(org.role)}
                            </Badge>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0 ml-2" onClick={(e) => e.stopPropagation()}>
                          {currentOrg?.id === org.id && (
                            <Check className="h-4 w-4 text-primary flex-shrink-0" />
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 hover:bg-muted hover:text-primary transition-colors flex-shrink-0"
                            onClick={() => handleTogglePin(org.id)}
                            title="Fixar organização"
                          >
                            <PinOff className="h-4 w-4 text-muted-foreground hover:text-primary transition-colors" />
                          </Button>
                          <Button
                            variant={currentOrg?.id === org.id ? "default" : "outline"}
                            size="sm"
                            onClick={() => handleSwitchOrganization(org.id)}
                            disabled={currentOrg?.id === org.id}
                            className="whitespace-nowrap flex-shrink-0 text-xs px-3 transition-colors organization-select-button"
                          >
                            {currentOrg?.id === org.id ? 'Atual' : 'Selecionar'}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Mensagem quando não há resultados */}
              {filteredOrgs.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  <Building2 className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Nenhuma organização encontrada</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

